
import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { MessageSquareQuoteIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

interface QuestionData {
  id: string;
  text: string;
  type: 'open' | 'closed' | 'toggle';
  category?: string;
  index?: number;
  isBig3?: boolean;
  isBackup?: boolean;
  isToggle?: boolean;
  isOpen?: boolean;
  onClick?: () => void;
}

interface QuestionNodeProps {
  data: QuestionData;
}

export const QuestionNode = memo(({ data }: QuestionNodeProps) => {
  // Toggle button styling
  if (data.isToggle) {
    return (
      <div 
        className="bg-gradient-to-r from-blue-100 to-indigo-100 border-2 border-blue-300 rounded-lg p-3 min-w-48 max-w-64 shadow-md cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
        onClick={data.onClick}
      >
        <div className="flex items-center justify-center space-x-2">
          {data.isOpen ? (
            <ChevronUpIcon className="w-5 h-5 text-blue-600" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-blue-600" />
          )}
          <span className="text-sm font-medium text-blue-800">
            {data.isOpen ? 'Hide backup questions' : 'Show backup questions'}
          </span>
        </div>
      </div>
    );
  }

  // Big 3 questions styling (larger, amber theme)
  if (data.isBig3) {
    return (
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-3 border-amber-400 rounded-xl p-6 min-w-80 max-w-96 shadow-xl animate-fade-in">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-full text-lg flex items-center justify-center font-bold shadow-md">
            {(data.index || 0) + 1}
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-3">
              <MessageSquareQuoteIcon className="w-5 h-5 text-amber-600" />
              <span className="text-sm font-bold text-amber-800 uppercase tracking-wide">
                Key Research Question
              </span>
            </div>
            <p className="text-base text-gray-800 leading-relaxed font-medium">
              {data.text}
            </p>
          </div>
        </div>
        
        <Handle 
          type="source" 
          position={Position.Bottom} 
          className="w-4 h-4 bg-amber-500 border-2 border-amber-600" 
        />
      </div>
    );
  }

  // Backup questions styling (smaller, blue theme)
  if (data.isBackup) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-4 min-w-56 max-w-72 shadow-md animate-fade-in">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-7 h-7 bg-blue-500 text-white rounded-full text-sm flex items-center justify-center font-medium">
            {(data.index || 0) + 1}
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <MessageSquareQuoteIcon className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-semibold text-blue-800 uppercase tracking-wide">
                Backup Question
              </span>
            </div>
            <p className="text-sm text-gray-800 leading-relaxed font-medium">
              {data.text}
            </p>
          </div>
        </div>
        
        <Handle 
          type="source" 
          position={Position.Bottom} 
          className="w-3 h-3 bg-blue-500 border-2 border-blue-600" 
        />
      </div>
    );
  }

  // Default styling (fallback)
  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-300 rounded-lg p-4 min-w-64 max-w-80 shadow-lg">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 w-8 h-8 bg-gray-600 text-white rounded-full text-sm flex items-center justify-center font-bold">
          {(data.index || 0) + 1}
        </div>
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <MessageSquareQuoteIcon className="w-4 h-4 text-gray-600" />
            <span className="text-xs font-semibold text-gray-800 uppercase tracking-wide">
              Question
            </span>
          </div>
          <p className="text-sm text-gray-800 leading-relaxed font-medium">
            {data.text}
          </p>
        </div>
      </div>
      
      <Handle 
        type="source" 
        position={Position.Bottom} 
        className="w-3 h-3 bg-gray-500 border-2 border-gray-600" 
      />
    </div>
  );
});

QuestionNode.displayName = 'QuestionNode';
